import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '@/content/sections'
import { EBOOKS, EBOOKS_INDEX, ebookHref } from '@/content/ebooks'
import { REPORTS, REPORTS_INDEX, reportHref } from '@/content/reports'

/**
 * Impact Labs — the hub.
 *
 * Built to the reference screenshots of `digitopia.co/impact-labs/`: five bands
 * in this order — Reports, Ebooks, Blog, the DAIMI course, Past Events — each a
 * heading, a one-line standfirst that ends in "Discover All", four cards, and
 * the same "Discover All" again as a button underneath.
 *
 * The imported scrape of this page (`content/imported/pages/en/impact-labs.json`)
 * had flattened all four grids into paragraphs of anchors pointing back at
 * digitopia.co, so it is superseded here — see `SUPERSEDED_BY_WRITTEN` in
 * `src/legacy.ts`.
 *
 * The report and e-book cards read the catalogues rather than restating them, so
 * a cover or a title corrected in `content/reports/*.ts` is corrected here too.
 */
export const route: RouteDef = { id: 'impactLabs', path: 'impact-labs', priority: 0.8 }

const report = (slug: string) => {
  const r = REPORTS.find((x) => x.slug === slug)
  return { title: r?.title ?? slug, href: reportHref('en', slug), cover: r?.cover }
}

const ebook = (slug: string) => {
  const b = EBOOKS.find((x) => x.slug === slug)
  return {
    title: b?.title ?? slug,
    href: b ? ebookHref('en', b) : `/en/${EBOOKS_INDEX}`,
    cover: b ? { src: b.cover.src, alt: b.cover.alt } : undefined,
  }
}

const DISCOVER = 'Discover All'

export const impactLabs: WrittenPage = {
  navLabel: 'Impact Labs',
  title: 'Impact Labs',
  description:
    'Reports, e-books, articles and events on digital and AI transformation: industry trends, future scenarios and benchmarks from Digitopia.',
  sections: [
    {
      kind: 'covers',
      heading: 'Reports',
      sub: 'Discover industry trends, envision future scenarios, and learn benchmarks.',
      link: { label: DISCOVER, href: `/en/${REPORTS_INDEX}` },
      cta: { label: DISCOVER, href: `/en/${REPORTS_INDEX}` },
      items: [
        report('digital-ai-maturity-report-2026'),
        report('digital-ai-maturity-report-usa-edition'),
        report('turkiye-dijital-ve-yapay-zeka-olgunluk-raporu-2026'),
        report('digital-and-ai-maturity-report-2026-airports-industry'),
      ],
    },
    {
      kind: 'covers',
      heading: 'Ebooks',
      sub: 'Unlock comprehensive guides on digital transformation, artificial intelligence, and sustainability.',
      link: { label: DISCOVER, href: `/en/${EBOOKS_INDEX}` },
      cta: { label: DISCOVER, href: `/en/${EBOOKS_INDEX}` },
      items: [
        ebook('agentic-ai'),
        ebook('mastering-communication-for-transformation'),
        ebook('importance-of-measurement-for-transformation-success'),
        ebook('ai-grocery-shopping'),
      ],
    },
    {
      kind: 'covers',
      heading: 'Blog',
      sub: 'Expand your knowledge on transformation with in-depth explanations and real-life stories.',
      link: { label: DISCOVER, href: '/en/blog' },
      cta: { label: DISCOVER, href: '/en/blog' },
      // The four posts the reference screenshot shows, with the excerpts it
      // shows. Each card carries the post's own artwork, supplied as the four
      // 400×400 covers the blog itself uses — a stand-in site photograph read
      // as a stock picture beside titles that clearly have designed covers.
      items: [
        {
          title:
            'From 1.9 to 4.2: Why Transformation Maturity Separates Laggards, Competitors, and Leaders',
          note: 'Transformation maturity is becoming a defining factor of future competitiveness. While fragmented co…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-maturity-1-9-to-4-2.webp',
            alt: 'Blog cover: 1.9 vs 3.1 vs 4.2 — how transformation maturity separates laggards, competitors and leaders on the DAIMI maturity framework',
          },
        },
        {
          title:
            'CIO AI Transformation Blog Series – 1. Master Your AI Transformation: Why CIOs Must Lead Enterprise AI',
          note: 'AI transformation requires more than pilots and tools. CIOs can guide the shift from scattered exper…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-cio-ai-transformation-01.webp',
            alt: 'Blog cover: CIO AI Transformation Blog Series 01 — Master your AI transformation, why CIOs must lead enterprise AI',
          },
        },
        {
          title:
            'CIO AI Transformation Blog Series – 2. How Ready Is Your Organization for AI Transformation?',
          note: 'AI transformation begins with readiness. CIOs need a clear way to measure maturity, expose gaps, ali…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-cio-ai-transformation-02.webp',
            alt: 'Blog cover: CIO AI Transformation Blog Series 02 — How ready is your organization for AI transformation',
          },
        },
        {
          title: 'Transformation Measurement: 7 Proven Ways to Stop Waste',
          note: 'Ambition starts transformation, transformation measurement sustains it. Here are seven principles th…',
          href: '/en/blog',
          cover: {
            src: '/photo/blog-transformation-measurement.webp',
            alt: 'Blog cover: colleagues following a lit path past rising bars, tracking maturity score progression across enterprise capabilities',
          },
        },
      ],
      check:
        'The four blog cards link to the blog index rather than to each post: the posts these four titles belong to were not among the ones imported under their own slugs. Point each card at its post once the article pages exist.',
    },
    {
      kind: 'split',
      heading: 'Get Certified and Join Lifelong Learners',
      paragraphs: [
        'Digital & AI Maturity Index Course',
        'Gain practical knowledge in digital and AI transformation maturity and position yourself as a trusted advisor in your field.',
      ],
      photo: {
        src: '/photo/learning-platform-tablet.webp',
        alt: 'The Digitopia Learning Platform sign-in screen on a tablet',
      },
      cta: { label: 'Learn more', href: '/en/digital-ai-maturity-index-daimi-course' },
    },
    {
      kind: 'covers',
      heading: 'Past Events',
      sub: 'Transformation decoded, paired with practical tips and essential strategies.',
      link: { label: DISCOVER, href: '/en/impact-labs/events' },
      cta: { label: DISCOVER, href: '/en/impact-labs/events' },
      items: [
        {
          title: 'Digitopia at BFSI Nexus Conference 2026',
          note: '4th – 5th May 2026 – The Westin Jersey City Newport, NJ',
          href: '/en/impact-labs/events',
        },
        {
          title: 'Digitopia at AI Next Las Vegas 2026',
          note: '21th – 22th May 2026 – Horseshoe Las Vegas, NV',
          href: '/en/impact-labs/events',
        },
        {
          title: 'AI Executive Masterclass – USA',
          note: 'May 6, Jersey City | May 15, Orlando | May 23, Las Vegas',
          href: '/en/impact-labs/events/ai-executive-masterclass',
        },
        {
          title: 'Dönüşümün Yapay Zeka Hali: Digitopia 2.0',
          note: '7 Nisan 2026 | 13:00 – 17:00 – Grand Hyatt Istanbul',
          href: '/en/impact-labs/events',
        },
      ],
    },
  ],
}
